import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { SET_MY_AFFILIATE_CUSTOM_CODE } from '@/queries/queries';

interface CustomLinkEditorProps {
  customCode: string | null;
  onSaved: () => void;
}

export const CustomLinkEditor = ({ customCode, onSaved }: CustomLinkEditorProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(customCode ?? '');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [save, { loading }] = useMutation(SET_MY_AFFILIATE_CUSTOM_CODE);

  const handleSave = async () => {
    setFieldError(null);
    try {
      await save({ variables: { code: draft.trim().toLowerCase() } });
      toast({ title: t('affiliates.link.customSaved') });
      setEditing(false);
      onSaved();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setFieldError(
        message.includes('taken')
          ? t('affiliates.link.customTaken')
          : t('affiliates.link.customInvalid'),
      );
    }
  };

  if (!editing) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setDraft(customCode ?? '');
          setFieldError(null);
          setEditing(true);
        }}
        data-testid="affiliate-customize-button"
      >
        <Pencil className="w-4 h-4 mr-2" />
        {t('affiliates.link.customize')}
      </Button>
    );
  }

  return (
    <div className="space-y-2" data-testid="affiliate-custom-editor">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground whitespace-nowrap">autoclipper.live/a/</span>
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={30}
          data-testid="affiliate-custom-input"
        />
        <Button size="sm" disabled={loading} onClick={handleSave} data-testid="affiliate-custom-save">
          {loading ? t('affiliates.link.customSaving') : t('affiliates.link.customSave')}
        </Button>
        <Button size="sm" variant="ghost" disabled={loading} onClick={() => setEditing(false)}>
          {t('affiliates.link.customCancel')}
        </Button>
      </div>
      {fieldError ? (
        <p className="text-sm text-destructive" data-testid="affiliate-custom-error">{fieldError}</p>
      ) : (
        <p className="text-sm text-muted-foreground">{t('affiliates.link.customHint')}</p>
      )}
      {customCode && <p className="text-xs text-muted-foreground">{t('affiliates.link.customWarning')}</p>}
    </div>
  );
};
